/**
 * "Your bank vs your twin, line by line": the year-end balance sheet and the year's income statement for you and the status-quo twin, and for each line the one
 * phrase (and the full list) of what put you apart, read from `lineAttribution`. Pure; Δ is always you − your twin, from the statements themselves.
 */
import type { ContentCatalog, RoundReport } from "@bsim/contracts";
import { formatUsdM } from "../format";
import type { TableRow } from "../financials/DataTable";
import { BALANCE_SPEC, INCOME_SPEC, balanceValues, buildRows, incomeValues, type LineSpec } from "../financials/statements";
import { keyLabel, type AttributionKey } from "./decomposition";

type Quarter = RoundReport["quarterly"][number];
export type Statement = "balance" | "income";
export type Values = Readonly<Record<string, number>>;
/** `decision`: the cause is a decision the player changed (not the market, earlier rounds or how they interact). */
export interface Cause { readonly label: string; readonly deltaUsd: number; readonly decision?: boolean }

export const SPECS: Readonly<Record<Statement, readonly LineSpec[]>> = { balance: BALANCE_SPEC, income: INCOME_SPEC };

/** The statement's values at the year's end (balance sheet) or summed over its quarters (income statement). */
export function yearValues(statement: Statement, quarters: readonly Quarter[]): Values {
  if (statement === "balance") return balanceValues((quarters.at(-1) as Quarter).statements);
  const out: Record<string, number> = {};
  for (const q of quarters) for (const [id, v] of Object.entries(incomeValues(q.statements))) out[id] = (out[id] ?? 0) + v;
  return out;
}

const STATIC_ITEMS: ReadonlySet<string> = new Set(["premises", "goodwill", "intangibles_cdi", "boli", "dta", "other_assets", "other_liabilities"]);
/** Lines the table shows as a cost (negative) that the engine's `lineAttribution` carries as a positive amount. */
const COST_PREFIX = ["ie.", "opex."];
const COST_IDS: ReadonlySet<string> = new Set(["acl", "provision"]);

/** The `lineAttribution` ids a table line adds up from, each with the sign that turns the engine's amount into the table's. */
function parts(id: string, spec: readonly LineSpec[]): readonly { readonly lineId: string; readonly sign: 1 | -1 }[] {
  const line = flat(spec).find((l) => l.id === id);
  if (line?.children !== undefined) return line.children.flatMap((c) => parts(c.id, spec));
  if (id === "nii") return ["interestIncome", "interestExpense", "swapNet"].flatMap((p) => parts(p, spec));
  if (id === "assets") return [{ lineId: "totalAssets", sign: 1 }];
  const sign = COST_IDS.has(id) || COST_PREFIX.some((p) => id.startsWith(p)) ? -1 : 1;
  const lineId = id.startsWith("ii.") ? `interestIncome.${id.slice(3)}` : id.startsWith("ie.") ? `interestExpense.${id.slice(3)}` : STATIC_ITEMS.has(id) ? `static.${id}` : id;
  const known = ["liabilities", "equity", "liabilitiesAndEquity", "pretax", "tax"];
  return known.includes(id) ? [] : [{ lineId, sign }];
}
const flat = (spec: readonly LineSpec[]): LineSpec[] => spec.flatMap((l) => [l, ...flat(l.children ?? [])]);

const sameKey = (a: AttributionKey, b: AttributionKey): boolean => (typeof a === "string" || typeof b === "string" ? a === b : a.decision === b.decision);

/** What put a line apart from the twin's, largest first; empty for a line the attribution does not cover. */
export function causesOf(report: RoundReport, statement: Statement, id: string, catalog: ContentCatalog): readonly Cause[] {
  const lines = report.decisionAttribution?.lineAttribution ?? [];
  const merged: { key: AttributionKey; deltaUsd: number }[] = [];
  for (const { lineId, sign } of parts(id, SPECS[statement])) {
    for (const r of lines.find((l) => l.lineId === lineId)?.rows ?? []) {
      const into = merged.find((m) => sameKey(m.key, r.key));
      if (into === undefined) merged.push({ key: r.key, deltaUsd: sign * r.deltaUsd }); else into.deltaUsd += sign * r.deltaUsd;
    }
  }
  return merged.filter((m) => Math.abs(m.deltaUsd) >= 50_000).sort((a, b) => Math.abs(b.deltaUsd) - Math.abs(a.deltaUsd)).map((m) => ({ label: keyLabel(m.key, catalog), deltaUsd: m.deltaUsd, decision: typeof m.key !== "string" }));
}

/** The line's whole gap to the twin's year as the engine states it (`lineAttribution[].gapUsd`, which its cause rows sum to exactly), in the table's sign; null when the attribution does not cover the line. */
export function gapOf(report: RoundReport, statement: Statement, id: string): number | null {
  const lines = report.decisionAttribution?.lineAttribution ?? [];
  const found = parts(id, SPECS[statement]).flatMap(({ lineId, sign }) => { const gap = lines.find((l) => l.lineId === lineId)?.gapUsd; return gap === undefined ? [] : [sign * gap]; });
  return found.length === 0 ? null : found.reduce((t, v) => t + v, 0);
}

const WHY_NAME: Readonly<Record<string, string>> = { "Carried from earlier years": "carried from earlier years", "How they interact": "how your choices interact" };
/** Mid-sentence a label starts lower-case, unless it is a name (second letter capital, as "CRE"). */
const midSentence = (label: string): string => (/^[A-Z][a-z]/.test(label) ? `${label.charAt(0).toLowerCase()}${label.slice(1)}` : label);
const named = (c: Cause): string => `${WHY_NAME[c.label] ?? midSentence(c.label)} (${formatUsdM(c.deltaUsd, { signed: true })})`;

/** Under this, a remainder is noise (the same floor as the cause list). */
const OTHER_FLOOR_USD = 50_000;

/**
 * One short phrase for what put a line apart from the twin's. Where some causes work against the gap, the parts would add to more than the total, so it says so as two figures: "Decisions: −$137M; offsets: +$52M"
 * (the causes with the gap's sign, then those against it, the engine's remainder included). With none against it, the largest one or two causes by name, "Mostly CRE growth (+$7.4M); carried from earlier rounds (+$2.1M)"
 * when those cover over half of the gap, else "Spread across" them.
 */
export function whyPhrase(causes: readonly Cause[], gapUsd: number | null = null, printedGapM: number | null = null): string {
  const [a, b] = causes;
  // The engine's cause rows sum to the line's gap exactly, so where the gap is not carried the rows' own sum stands in for it.
  const gap = gapUsd ?? causes.reduce((t, c) => t + c.deltaUsd, 0);
  if (a === undefined) return "\u2014";
  const top = [a, b].flatMap((c) => (c === undefined ? [] : [c]));
  const rest = gap - causes.reduce((t, c) => t + c.deltaUsd, 0);
  const parts: readonly Cause[] = [...causes, ...(Math.abs(rest) >= OTHER_FLOOR_USD ? [{ label: "other causes", deltaUsd: rest }] : [])];
  const against = parts.filter((c) => c.deltaUsd * gap < 0).reduce((t, c) => t + c.deltaUsd, 0);
  if (Math.abs(against) >= OTHER_FLOOR_USD) {
    const same = parts.filter((c) => c.deltaUsd * gap >= 0);
    const sameM = Math.round(same.reduce((t, c) => t + c.deltaUsd, 0) / 1e6);
    // The two figures add to the printed gap beside them: the offsets are the printed gap less the printed same-way figure, and a zero is not a figure.
    const offsetM = (printedGapM ?? Math.round(gap / 1e6)) - sameM;
    const decisions = same.every((c) => c.decision === true);
    if (sameM === 0 && offsetM === 0) return "Nothing to split this year";
    if (offsetM === 0) return `${decisions ? "Decisions" : "For"}: ${wholeM(sameM)}`;
    return decisions ? `Decisions: ${wholeM(sameM)}; offsets: ${wholeM(offsetM)}` : `For: ${wholeM(sameM)}; against: ${wholeM(offsetM)}`;
  }
  const shown = top.reduce((t, c) => t + c.deltaUsd, 0);
  const other = Math.abs(gap - shown) >= OTHER_FLOOR_USD ? `; other causes (${formatUsdM(gap - shown, { signed: true })})` : "";
  return `${Math.abs(shown) > Math.abs(gap) / 2 ? "Mostly" : "Spread across"} ${top.map(named).join("; ")}${other}`;
}
/** Whole $ millions with the sign, as the two-figure phrase reads. */
const wholeM = (m: number): string => (m === 0 ? "$0M" : `${m < 0 ? "\u2212" : "+"}$${Math.abs(m)}M`);
/** A printed change-column cell ("+351", "\u22124", "\u2014") as whole millions. */
const millionsOf = (cell: unknown): number | null => (typeof cell !== "string" ? null : cell === "\u2014" ? 0 : Number.isNaN(Number(cell.replace("\u2212", "-").replace(/[,+]/g, ""))) ? null : Number(cell.replace("\u2212", "-").replace(/[,+]/g, "")));

/** The comparison table's rows: You · Your twin · Δ · Why. `minGapUsd` leaves the Why empty on a line whose gap to the twin is smaller (the printed pack names causes only on lines that matter). */
export function compareRows(report: RoundReport, twin: readonly Quarter[], statement: Statement, catalog: ContentCatalog, minGapUsd = 0): TableRow[] {
  const whyOf = (id: string, printedGapM: number | null): string => {
    const causes = causesOf(report, statement, id, catalog);
    const gap = gapOf(report, statement, id) ?? causes.reduce((t, c) => t + c.deltaUsd, 0);
    return Math.abs(gap) < minGapUsd ? "" : whyPhrase(causes, gap, printedGapM);
  };
  // The change column is the difference of the printed figures; the Why cell adds to that one.
  const why = (rows: readonly TableRow[]): TableRow[] => rows.map((r) => ({
    ...r, cells: [...r.cells, whyOf(r.id, millionsOf(r.cells[2]))], ...(r.children === undefined ? {} : { children: why(r.children) }),
  }));
  return why(buildRows(SPECS[statement], yearValues(statement, report.quarterly), yearValues(statement, twin)));
}
